import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface BlogObject {
  id: number;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  image: string;
  seo: JSON;
  createdAt: Date;
  publishedAt: Date;
  status: string;
  isHomepage: boolean;
  isActive: boolean;
  isDeleted: boolean;
  authorName: string;
  authorAvatar: string;
  sourceName: string;
  sourceLink: string;
  contentEn: string;
  contentAr: string;
  readingTime: number;
  externalReference: string;
  isExternal: boolean;
}

export interface BlogsListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  blogs: Array<BlogObject>;
  errors: unknown;
}

@Module
export default class BlogsModule extends VuexModule implements BlogsListInfo {
  blogs = [] as Array<BlogObject>;
  allBlogs = [] as Array<BlogObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get blogs list
   * @returns Array<BlogObject>
   */
  get blogsList(): Array<BlogObject> {
    return this.blogs;
  }

  /**
   * Get total items
   * @returns number
   */
  get blogsListCount(): number {
    return this.totalItems;
  }

  get getAllBlogsList(): Array<BlogObject> {
    return this.allBlogs;
  }

  @Mutation
  [Mutations.SET_BLOGS](data) {
    this.blogs = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_BLOGS](data) {
    this.allBlogs = data.data;
  }

  @Mutation
  [Mutations.SET_BLOGS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_BLOGS_LIST]() {
    this.blogs = [] as Array<BlogObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_BLOGS_STORE]() {
    this.context.commit(Mutations.RESET_BLOGS_LIST);
  }

  @Action
  [Actions.CREATE_BLOG](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/blogs', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_BLOGS_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_BLOG](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/blogs', id)
      .then(() => {
        this.context.commit(Mutations.RESET_BLOGS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_BLOG]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/blogs', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_BLOGS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_BLOG_CONTENT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.post(`/blogs/content?id=${id}`, data)
      .then(() => {
        this.context.commit(Mutations.RESET_BLOGS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_BLOG_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_BLOGS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_BLOG](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/blogs', id);
  }

  @Action
  [Actions.GET_BLOGS]() {
    ApiService.setAuthorizationHeader();
    const params = { page: this.currentPage, limit: 10 };
    return ApiService.query('/blogs', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_BLOGS, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.GET_ALL_BLOGS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/blogs/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_BLOGS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }
  @Action
  [Actions.GET_BLOG_ASSOCIATED_TAGS](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/blogs/tags', id);
  }

  @Action
  [Actions.UPLOAD_BLOG_COVER_IMAGE]({ blogId, data }) {
    console.log(blogId, data);
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/blogs/uploadCover/${blogId}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPLOAD_BLOG_AUTHOR_AVATAR]({ blogId, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/blogs/uploadAuthorAvatar/${blogId}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
}
